import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Loader2, RefreshCw, DollarSign, Package, Mic, Ticket, Mail, Dog, Cat, Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACK_LABEL = { single: '1-vent', pack5: '5-pack', pack10: '10-pack' };

const StatCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-2xl bg-zinc-900/70 border border-white/10 p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-emerald-300 mb-3">
      <Icon className="w-4 h-4" />
      <span className="text-xs uppercase tracking-wider text-zinc-400">{label}</span>
    </div>
    <div className="text-3xl font-heading font-bold text-white">{value}</div>
    {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
  </div>
);

const RevenueAdminPage = () => {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const load = async (pw) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/stats?password=${encodeURIComponent(pw)}`);
      setStats(res.data);
      setAuthed(true);
      setPwError('');
    } catch (err) {
      if (err.response?.status === 401) { setPwError('Wrong password'); setAuthed(false); }
      else setPwError('Failed to load: ' + (err.message || 'unknown'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => { e.preventDefault(); if (password) load(password); };

  if (!authed) {
    return (
      <div className="min-h-screen bg-vent-bg bg-pattern flex items-center justify-center px-5">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-zinc-900 border border-emerald-400/20 rounded-2xl p-6" data-testid="revenue-login-form">
          <div className="flex items-center gap-2 mb-4 text-white">
            <Lock className="w-5 h-5 text-emerald-400" />
            <h1 className="font-heading font-bold text-xl">Revenue Dashboard</h1>
          </div>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            data-testid="revenue-password-input"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:border-emerald-400/50 outline-none mb-3"
          />
          {pwError && <p className="text-sm text-red-400 mb-3" data-testid="revenue-pw-error">{pwError}</p>}
          <button type="submit" disabled={loading} data-testid="revenue-login-btn" className="w-full py-3 rounded-xl bg-emerald-400 text-[#08130d] font-heading font-bold hover:scale-[1.02] transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enter'}
          </button>
        </form>
      </div>
    );
  }

  const s = stats || {};
  const packs = s.packs_by_type || {};
  const vents = s.vents || {};
  const top = s.top_buddies || [];
  const maxVents = Math.max(1, ...top.map((b) => b.vents));

  return (
    <div className="min-h-screen bg-vent-bg bg-pattern px-4 sm:px-6 py-10" data-testid="revenue-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-white">Revenue & Engagement</h1>
            <p className="text-zinc-500 text-sm mt-1">Vent to a Dog · live metrics</p>
          </div>
          <button onClick={() => load(password)} disabled={loading} data-testid="revenue-refresh-btn" className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800 text-white text-sm hover:bg-zinc-700 transition-colors disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard icon={DollarSign} label="Revenue" value={`$${(s.revenue || 0).toFixed(2)}`} sub={`${s.currency || 'USD'} · gross`} testid="stat-revenue" />
          <StatCard icon={Package} label="Packs sold" value={s.packs_sold || 0} sub={Object.entries(packs).map(([k, v]) => `${PACK_LABEL[k] || k}: ${v}`).join(' · ') || 'none yet'} testid="stat-packs" />
          <StatCard icon={Ticket} label="Vents purchased" value={s.vents_sold || 0} sub={`${s.credits_outstanding || 0} unused`} testid="stat-vents-sold" />
          <StatCard icon={Mail} label="Subscribers" value={s.subscribers_total || 0} sub="email list" testid="stat-subs" />
        </div>

        <div className="grid grid-cols-3 gap-4 mb-8">
          <StatCard icon={Mic} label="Total vents" value={vents.total || 0} testid="stat-vents-total" />
          <StatCard icon={Mic} label="Free vents" value={vents.free || 0} testid="stat-vents-free" />
          <StatCard icon={Mic} label="Paid vents" value={vents.paid || 0} testid="stat-vents-paid" />
        </div>

        <div className="rounded-2xl bg-zinc-900/70 border border-white/10 p-5" data-testid="top-buddies">
          <div className="flex items-center gap-2 text-emerald-300 mb-4">
            <Trophy className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider text-zinc-400">Top buddies by vents</span>
          </div>
          {top.length === 0 ? (
            <p className="text-zinc-600 text-sm">No vents yet.</p>
          ) : (
            <div className="space-y-3">
              {top.map((b) => (
                <div key={b.slug} className="flex items-center gap-3" data-testid={`buddy-row-${b.slug}`}>
                  <div className="w-24 shrink-0 flex items-center gap-1.5 text-sm text-white">
                    {b.species === 'cat' ? <Cat className="w-4 h-4 text-zinc-400" /> : <Dog className="w-4 h-4 text-zinc-400" />}
                    <span className="truncate">{b.name}</span>
                  </div>
                  <div className="flex-1 h-6 rounded-full bg-black/30 overflow-hidden">
                    <div className="h-full bg-emerald-400/80 rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max(8, (b.vents / maxVents) * 100)}%` }}>
                      <span className="text-[11px] font-bold text-[#08130d]">{b.vents}</span>
                    </div>
                  </div>
                  <div className="w-20 shrink-0 text-right text-xs text-emerald-300">{b.paid_vents} paid</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueAdminPage;
